import { useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, CheckCircle } from "lucide-react";

const Privacy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Introduction",
            content: [
                "Kuberya AI (\"we,\" \"our,\" or \"Company\") operates Parakh, an AI-powered UPSC answer evaluation platform. This Privacy Policy explains how we collect, use, disclose, and protect your personal information.",
                "We are committed to protecting your privacy and ensuring transparency about our data practices. This policy applies to all users of parakh-ai-rho.vercel.app."
            ]
        },
        {
            title: "2. Information We Collect",
            subsections: [
                {
                    subtitle: "2.1 Information You Provide Directly",
                    points: [
                        "Account Information: Name, email address, phone number (optional), and encrypted password.",
                        "Profile Information: Details you provide to customize your experience.",
                        "Payment Information: Billing details and transaction history. We do not store complete credit/debit card numbers.",
                        "Content You Upload: Answer sheets (PDF, images), text responses, queries, and feedback."
                    ]
                },
                {
                    subtitle: "2.2 Information Collected Automatically",
                    points: [
                        "Usage Data: Pages visited, features used, time spent, and evaluation history.",
                        "Device Info: Browser type, version, device information, IP address, and location data.",
                        "Cookies: Data collected via cookies and similar technologies.",
                        "Analytics: Performance metrics, feature usage patterns, and error logs."
                    ]
                }
            ]
        },
        {
            title: "3. How We Use Your Information",
            subsections: [
                {
                    subtitle: "Provide Services",
                    points: ["Process and evaluate answer sheets using AI.", "Generate feedback reports.", "Track progress and maintain history."]
                },
                {
                    subtitle: "Improve Our Platform",
                    points: ["Train AI evaluation models.", "Develop new features.", "Analyze usage patterns to optimize performance."]
                },
                {
                    subtitle: "Communicate With You",
                    points: ["Send evaluation results.", "Provide support.", "Share important updates.", "Send promotional communications (with consent)."]
                },
                {
                    subtitle: "Security & Compliance",
                    points: ["Detect fraud.", "Ensure platform security.", "Comply with legal obligations.", "Enforce Terms."]
                }
            ]
        },
        {
            title: "4. Legal Basis for Processing",
            content: [
                "We process your data based on:",
                "• Consent: When you explicitly agree to specific processing.",
                "• Contract Performance: To provide the services you've subscribed to.",
                "• Legitimate Interests: To improve our services and prevent fraud.",
                "• Legal Obligations: To comply with Indian laws and regulations."
            ]
        },
        {
            title: "5. How We Share Your Information",
            content: [
                "We DO NOT sell your personal information.",
                "We may share your information with:",
                "• Service Providers: Cloud hosting, payment processors, email services, and analytics tools.",
                "• AI Training: Anonymized answer sheets may be used to improve AI models (personal identifiers removed).",
                "• Legal Requirements: When required by law, court order, or to protect our rights.",
                "• With Your Consent: When you explicitly authorize us or participate in community features."
            ]
        },
        {
            title: "6. Data Security",
            content: [
                "We implement industry-standard security measures, including:",
                "• Technical Safeguards: SSL/TLS encryption for data in transit, encrypted storage, and regular security audits.",
                "• Organizational Safeguards: Limited employee access, security training, and data breach protocols.",
                "Note: No system is completely secure. We cannot guarantee absolute security."
            ]
        },
        {
            title: "7. Data Retention",
            content: [
                "We retain your information as long as necessary:",
                "• Evaluation History: Retained while your account is active.",
                "• Account Data: Deleted within 90 days of account closure.",
                "• Payment Records: Retained for 7 years per Indian tax regulations.",
                "You may request data deletion at any time, subject to legal requirements."
            ]
        },
        {
            title: "8. Your Privacy Rights",
            content: [
                "You have the right to:",
                "• Access: Request copies of your personal data.",
                "• Correction: Update inaccurate information.",
                "• Deletion: Request account and data deletion.",
                "• Portability: Receive data in a structured format.",
                "• Objection/Withdrawal: Object to processing or withdraw consent.",
                "Contact us at kuberyaai@gmail.com to exercise these rights."
            ]
        },
        {
            title: "9. Cookies and Tracking",
            content: [
                "We use cookies to keep you logged in, remember preferences, and analyze usage. You can control cookies via browser settings, though this may affect functionality."
            ]
        },
        {
            title: "10. Children's Privacy",
            content: [
                "Parakh is intended for users aged 16+. We do not knowingly collect data from children under 16 and will delete such data if discovered."
            ]
        },
        {
            title: "11. Third-Party Links",
            content: [
                "Our platform may contain links to external sites (e.g., Telegram). We are not responsible for their privacy practices."
            ]
        },
        {
            title: "12. International Transfers",
            content: [
                "Data is primarily stored in India. International transfers, if any, are protected by appropriate safeguards."
            ]
        },
        {
            title: "13. Changes to Policy",
            content: [
                "We may update this policy periodically. Significant changes will be communicated via email or platform announcements. Continued use implies acceptance."
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-20 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-[2.5rem] shadow-xl p-8 md:p-12"
                >
                    {/* Header */}
                    <div className="text-center mb-16 space-y-4">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 items-center justify-center rounded-full text-indigo-700 font-bold text-sm uppercase tracking-wider mb-4">
                            <Lock className="w-4 h-4" />
                            Data Protection
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
                            Privacy <span className="text-indigo-600">Policy</span>
                        </h1>
                     
                    </div>

                    {/* Content */}
                    <div className="space-y-12">
                        {sections.map((section, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                            >
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">
                                        {idx + 1}
                                    </span>
                                    {section.title.split('. ')[1]}
                                </h2>

                                <div className=" space-y-6">
                                    {section.content?.map((paragraph, i) => (
                                        <p key={i} className={`text-gray-600 leading-relaxed ${paragraph.startsWith('•') ? 'pl-4' : ''}`}>
                                            {paragraph}
                                        </p>
                                    ))}

                                    {section.subsections?.map((sub, i) => (
                                        <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h3 className="font-bold text-gray-900 mb-3 text-lg">{sub.subtitle}</h3>
                                            <ul className="space-y-2">
                                                {sub.points.map((point, k) => (
                                                    <li key={k} className="flex items-start gap-2 text-gray-600 text-sm">
                                                        <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                                                        <span>{point}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}

                        {/* Contact Section */}
                        <div className="mt-16 p-8 bg-indigo-50 rounded-3xl border border-indigo-100">
                            <h2 className="text-xl font-bold text-indigo-900 mb-6 flex items-center gap-2">
                                <Mail className="w-5 h-5" />
                                14. Contact & Support
                            </h2>
                            <p className="text-indigo-700/80 mb-6">For privacy-related questions or to exercise your rights:</p>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Email Us</span>
                                    <a href="mailto:kuberyaai@gmail.com" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        kuberyaai@gmail.com
                                    </a>
                                </div>
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Phone</span>
                                    <a href="tel:+919009022058" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        +91 9009022058
                                    </a>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Address</span>
                                    <p className="text-indigo-900 font-medium leading-relaxed">
                                        SBI Chowk, Jabalpur, Madhya Pradesh, India
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Privacy;
